import { useState, useMemo } from "react";
import { useAuth } from "../auth/AuthContext";
import { useNavigate } from "react-router-dom";
import Chart from "chart.js";
import { Line, Bar } from "react-chartjs-2";
import LoadingSpinner from "components/LoadingSpinner";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  NavItem,
  NavLink,
  Nav,
  Progress,
  Table,
  Container,
  Row,
  Col,
  CardTitle,
} from "reactstrap";

import {
  chartOptions,
  parseOptions,
  chartExample1,
  chartExample2,
} from "variables/charts.js";

/* ===== SVG ICONS (CRA) ===== */
import { ReactComponent as HomeIcon } from "../assets/icons/house-svgrepo-com.svg";
import { ReactComponent as CustomerIcon } from "../assets/icons/man-svgrepo-com.svg";
import { ReactComponent as ProductIcon } from "../assets/icons/box-svgrepo-com.svg";
import { ReactComponent as ServiceIcon } from "../assets/icons/heart-svgrepo-com.svg";
import { ReactComponent as BillIcon } from "../assets/icons/shopping-card-svgrepo-com.svg";
import { ReactComponent as OrgIcon } from "../assets/icons/building-svgrepo-com.svg";
import { ReactComponent as BranchIcon } from "../assets/icons/shop-svgrepo-com.svg";
import { ReactComponent as EmployeeIcon } from "../assets/icons/id-card-svgrepo-com.svg";
import { ReactComponent as SettingIcon } from "../assets/icons/wrench-svgrepo-com.svg";
import { ReactComponent as MarketPlaceIcon } from "../assets/icons/shopping-bag-svgrepo-com.svg";
import { ReactComponent as BussinessTypeIcon } from "../assets/icons/briefcase-svgrepo-com.svg";

/* ===== TOP ICON MENU ===== */
const TopIconMenu = ({ items, activeKey, onChange, onNavigate }) => (
  <div className="zi-topmenu">
    <div className="zi-topmenu__scroll">
      {items.map((it) => {
        const Icon = it.icon;
        const active = it.key === activeKey;

        return (
          <button
            key={it.key}
            type="button"
            className={`zi-topmenu__item ${active ? "is-active" : ""}`}
            style={{
              // ✅ สีหลักต่อปุ่ม
              "--zi-accent": it.color,
            }}
            onClick={() => {
              onChange(it.key);
              onNavigate(it.path);
            }}
          >
            <span className="zi-topmenu__icon">
              <Icon className="zi-topmenu__svg" />
            </span>
            <span className="zi-topmenu__label">{it.label}</span>
          </button>
        );
      })}
    </div>

    <style>{`
.zi-topmenu{
  padding: 0 !important;
  margin-bottom: 20px;
}

/* ===== Layout ===== */
.zi-topmenu__scroll{ display:flex; flex-wrap:wrap; gap:16px; width:100%; max-width:1200px; justify-content:center; }

.zi-topmenu__item{
  flex:0 0 calc(33.333% - 16px);
  height:130px;
  border-radius:18px;
  background: color-mix(in srgb, var(--zi-accent) 80%, white);
  border:none;
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:center;
  gap:10px;
  cursor:pointer;
  transition: all .15s ease;
}

/* Hover */
.zi-topmenu__item:hover{
  filter: brightness(0.95);
  transform: translateY(-2px);
  box-shadow: 0 14px 26px rgba(0,0,0,.15);
}

/* ===== Icon ===== */
.zi-topmenu__icon{
  width: 52px;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;              /* ✅ ขาว */
}

.zi-topmenu__svg{
  width: 38px !important;
  height: 38px !important;
}

/* ===== Label ===== */
.zi-topmenu__label{
  font-size: 18px;
  font-weight: 600;
  color: #fff;              /* ✅ ขาว */
  text-align: center;
  line-height: 1.2;
  white-space: nowrap;
}

/* ===== Active ===== */
.zi-topmenu__item.is-active{
  outline: 3px solid rgba(255,255,255,.35);
  box-shadow: 0 18px 32px rgba(0,0,0,.25);
}

/* ===== Tablet ===== */
@media (max-width: 991.98px){
  .zi-topmenu__item{
    flex: 0 0 calc(50% - 16px);
    height: 120px;
  }
}

/* ===== Mobile ===== */
@media (max-width: 575.98px){
  .zi-topmenu__item{
    flex: 0 0 100%;
    height: 110px;
  }
}

.zi-topmenu{
  padding: 0 !important;
  margin-bottom: 0;          /* ✅ ไม่ดันลง */
  width: 100%;
  display: flex;
  justify-content: center;    /* ✅ กลางซ้าย-ขวา */
}

.zi-topmenu__scroll{
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  width: 100%;
  max-width: 1200px;          /* ✅ คุมความกว้างให้สวย */
  justify-content: center;    /* ✅ ปุ่มอยู่กลาง */
}

`}</style>
  </div>
);

const SubMenuProductService = (props) => {
  const navigate = useNavigate();

  const [activeNav, setActiveNav] = useState(1);
  const [chartExample1Data, setChartExample1Data] = useState("data1");

  // ✅ ตั้ง default active ให้ตรงกับ key จริง
  const [activeCategory, setActiveCategory] = useState("box");

  const { user } = useAuth();
  const isAdmin = (user?.role ?? "").toLowerCase() === "admin";

  if (window.Chart) {
    parseOptions(Chart, chartOptions());
  }

  const toggleNavs = (e, index) => {
    e.preventDefault();
    setActiveNav(index);
    setChartExample1Data("data" + index);
  };

  const adminMenuItems = [
    {
      key: "business",
      label: "ตั้งค่าธุรกิจลูกค้า",
      icon: BussinessTypeIcon,
      path: "/business-type",
      color: "#A9A9A9",
    },
    {
      key: "customer",
      label: "ตั้งค่าข้อมูลลูกค้า",
      icon: CustomerIcon,
      path: "/customer",
      color: "#A9A9A9",
    },
    {
      key: "org",
      label: "ตั้งค่าองค์กร",
      icon: OrgIcon,
      path: "/organization",
      color: "#A9A9A9",
    },
    {
      key: "branch",
      label: "ตั้งค่าสาขา",
      icon: BranchIcon,
      path: "/branch",
      color: "#A9A9A9",
    },
    {
      key: "employee",
      label: "ตั้งค่าผู้ใช้",
      icon: EmployeeIcon,
      path: "/employee",
      color: "#A9A9A9",
    },
    {
      key: "setting",
      label: "ตั้งค่าทั่วไป",
      icon: SettingIcon,
      path: "/setting",
      color: "#A9A9A9",
    },
  ];

  const userMenuItems = [
    {
      key: "customer",
      label: "ตั้งค่าข้อมูลลูกค้า",
      icon: CustomerIcon,
      path: "/customer",
      color: "#A9A9A9",
    },
    {
      key: "branch",
      label: "ตั้งค่าสาขา",
      icon: BranchIcon,
      path: "/edit-organization-user",
      color: "#A9A9A9",
    },
    {
      key: "setting",
      label: "ตั้งค่าทั่วไป",
      icon: SettingIcon,
      path: "/setting",
      color: "#A9A9A9",
    },
  ];

  const topMenuItems = useMemo(() => {
    return [...(isAdmin ? adminMenuItems : userMenuItems)];
  }, [isAdmin]);

  const isMobile = window.innerWidth < 768;

  return (
    <>
      <div
        className="header"
        style={{
          minHeight: "100vh",
          background: "#f7fafc",
          position: "relative", // ✅ สำคัญมาก
        }}
      >
        <Button
          color="info"
          onClick={() => navigate("/index")}
          type="button"
          style={{
            position: "absolute",
            top: isMobile ? 30 : 100,
            left: isMobile ? 16 : 24,
            zIndex: 10,
          }}
        >
          กลับหน้าแรก
        </Button>

        {/* เนื้อหาหลัก (การ์ดสินค้า/บริการ) */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
          }}
        >
          <Container
            fluid
            style={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <TopIconMenu
              items={topMenuItems}
              activeKey={activeCategory}
              onChange={setActiveCategory}
              onNavigate={(path) => path && navigate(path)}
            />
          </Container>
        </div>
      </div>
    </>
  );
};

export default SubMenuProductService;
